import { useEffect, useState } from 'react'
import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { selectedBorder } from './selectedBorder.js'
import {
  sequenceCount, sequenceHandle, SEQUENCE_MIN_OUTPUTS, SEQUENCE_MAX_OUTPUTS,
} from '../../lib/sequenceNode.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { fieldNumber } from '../../lib/fieldNumber.js'

// ── Make Literal ──────────────────────────────────────────────────────────────
const LITERAL_META = {
  makeLiteralString: { label: 'Make Literal String', accent: '#c060e8', inputType: 'text',   step: undefined },
  makeLiteralFloat:  { label: 'Make Literal Float',  accent: '#60a0d0', inputType: 'number', step: 0.1       },
  makeLiteralInt:    { label: 'Make Literal Int',    accent: '#60d090', inputType: 'number', step: 1         },
}

export function MakeLiteralNode({ type, data, selected }) {
  const meta    = LITERAL_META[type] ?? LITERAL_META.makeLiteralString
  const accent  = meta.accent
  const stop    = e => e.stopPropagation()
  const value   = data.value ?? (meta.inputType === 'text' ? '' : 0)

  return (
    <div style={{
      background: selected ? '#0e1218' : '#080c10',
      border: `2px solid ${selected ? selectedBorder(accent + '44') : accent + '44'}`,
      borderRadius: 6,
      minWidth: 200,
      fontFamily: 'var(--m-font)',
      boxShadow: 'none',
    }}>
      {/* Header */}
      <div style={{
        background: accent + '18',
        padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 13 }}>ƒ</span>
        <span style={{ color: accent, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>
          {meta.label}
        </span>
      </div>

      {/* Value row — input pin on left, output pin on right */}
      <div style={{
        padding: '8px 12px',
        display: 'flex', alignItems: 'center', gap: 8,
        position: 'relative',
      }}>
        <Handle
          type="target"
          position={Position.Left}
          id="in-value"
          style={{ background: accent, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${accent}66` }}
        />
        <span style={{ color: accent + '88', fontSize: 10, minWidth: 36 }}>Value</span>
        <input className={nodeControlClass()}
          type={meta.inputType}
          step={meta.step}
          value={value}
          onChange={e => data.onChange?.('value', meta.inputType === 'number' ? (meta.step === 1 ? parseInt(e.target.value) || 0 : parseFloat(e.target.value) || 0) : e.target.value)}
          onMouseDown={stop}
          style={{
            flex: 1, background: '#040608', border: `1px solid ${accent}33`,
            color: accent, padding: '3px 6px', borderRadius: 3,
            fontSize: 11, fontFamily: 'var(--m-mono)', outline: 'none', textAlign: meta.inputType === 'number' ? 'right' : 'left',
          }}
        />
        <span style={{ color: accent + '88', fontSize: 10, whiteSpace: 'nowrap' }}>Return Value</span>
        <Handle
          type="source"
          position={Position.Right}
          id="out-value"
          style={{ background: accent, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${accent}66` }}
        />
      </div>
    </div>
  )
}

// ── Delay ─────────────────────────────────────────────────────────────────────
// Teal scheme (action node), same family as PlayFirstSentence
const D_HEADER_BG     = '#0a3028'
const D_HEADER_ACTIVE = '#10503a'
const D_ACCENT        = '#40d090'
const D_BORDER_SEL    = '#40d090'
const D_BORDER_IDLE   = '#0d4030'

export function DelayNode({ data, selected }) {
  const fired = isFired(data)
  const stop  = e => e.stopPropagation()
  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(D_BORDER_IDLE) : D_BORDER_IDLE}`,
      borderRadius: 6,
      minWidth: 190,
      fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      {/* Header */}
      <div style={{
        background: selected ? D_HEADER_ACTIVE : D_HEADER_BG,
        padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>ƒ</span>
        <span style={{ color: D_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Delay</span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>
      {/* Duration — wirable pin + fallback input */}
      <div style={{ padding: '8px 12px', position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Handle
          type="target"
          position={Position.Left}
          id="in-duration"
          style={{ background: '#60b0d0', width: 10, height: 10, borderRadius: '50%', border: '2px solid #60b0d044' }}
        />
        <span style={{ color: '#2a6050', fontSize: 10, minWidth: 58 }}>Duration</span>
        <input className={nodeControlClass()}
          type="number" min={0.1} max={60} step={0.1}
          value={data.duration ?? 1}
          onChange={e => data.onChange?.('duration', fieldNumber(e.target.value))}
          onMouseDown={stop}
          style={{
            flex: 1,
            background: '#020a06', border: '1px solid #0d3020',
            color: D_ACCENT, padding: '3px 6px',
            borderRadius: 3, fontSize: 11,
            fontFamily: 'var(--m-mono)', outline: 'none', textAlign: 'right',
          }}
        />
      </div>
    </div>
  )
}

// ── Branch (If) ───────────────────────────────────────────────────────────────
const B_HEADER_BG   = '#1a1a2e'
const B_ACCENT      = '#a0a0d0'
const B_BORDER_SEL  = '#6060a0'
const B_BORDER_IDLE = '#2a2a40'
const B_PIN_BOOL    = '#d06060'

export function BranchNode({ data, selected }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? '#14141e' : '#0e0e18',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(B_BORDER_IDLE) : B_BORDER_IDLE}`,
      borderRadius: 6,
      minWidth: 180,
      fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      {/* Header — exec-in only */}
      <div style={{
        background: B_HEADER_BG,
        padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>🔀</span>
        <span style={{ color: B_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Branch</span>
      </div>
      {/* Condition pin (left) + True exec-out (right) */}
      <div style={{
        padding: '7px 12px', position: 'relative',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid #1a1a28',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Handle type="target" position={Position.Left} id="in-condition"
            style={{ background: B_PIN_BOOL, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${B_PIN_BOOL}66` }} />
          <span style={{ color: '#4a4a70', fontSize: 9 }}>Condition</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ color: '#40c040', fontSize: 9, fontWeight: 'bold' }}>True</span>
          <Handle type="source" position={Position.Right} id="execTrue"
            style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        </div>
      </div>
      {/* False exec-out (right) */}
      <div style={{
        padding: '7px 12px', position: 'relative',
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ color: '#c04040', fontSize: 9, fontWeight: 'bold' }}>False</span>
          <Handle type="source" position={Position.Right} id="execFalse"
            style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        </div>
      </div>
    </div>
  )
}

// ── Sequence ──────────────────────────────────────────────────────────────────
// Unreal's Sequence: one exec in, "Then 0..N" out, all fired in order every
// time. Same look as Branch (both are flow control). Count rules + pin ids live
// in lib/sequenceNode.js so this and the runtime agree on which pins exist.
export const SEQUENCE_ACCENT = B_ACCENT

const SEQ_EXEC = { background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }
const seqStop = e => e.stopPropagation()

function SeqPinButton({ label, title, onClick, disabled }) {
  return (
    <button
      className="nodrag"
      title={title}
      onPointerDown={seqStop} onMouseDown={seqStop}
      onClick={e => { e.stopPropagation(); onClick() }}
      disabled={disabled}
      style={{
        background: 'transparent', border: 'none', padding: '0 2px',
        color: disabled ? '#3a3a58' : '#7a7aa8', cursor: disabled ? 'default' : 'pointer',
        fontSize: 9, fontFamily: 'var(--m-font)',
      }}
    >{label}</button>
  )
}

export function SequenceNode({ data, selected }) {
  const fired = isFired(data)
  const count = sequenceCount(data)
  const setCount = n => data.onChange?.('count', sequenceCount({ count: n }))
  return (
    <div style={{
      background: selected ? '#14141e' : '#0e0e18',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(B_BORDER_IDLE) : B_BORDER_IDLE}`,
      borderRadius: 6,
      minWidth: 150,
      fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: B_HEADER_BG,
        padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
        borderRadius: '4px 4px 0 0',
      }}>
        <Handle type="target" position={Position.Left} id="execIn" style={SEQ_EXEC} />
        <span style={{ color: B_ACCENT, fontSize: 12, lineHeight: 1 }}>☰</span>
        <span style={{ color: B_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Sequence</span>
      </div>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{
          padding: '5px 12px', position: 'relative',
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 5,
        }}>
          <span style={{ color: '#8a8ab8', fontSize: 9 }}>Then {i}</span>
          <Handle type="source" position={Position.Right} id={sequenceHandle(i)} style={SEQ_EXEC} />
        </div>
      ))}
      <div style={{
        padding: '3px 10px 5px', display: 'flex', justifyContent: 'flex-end', gap: 6,
        borderTop: '1px solid #1a1a28',
      }}>
        <SeqPinButton label="− Remove pin" title="Remove the last output"
          onClick={() => setCount(count - 1)} disabled={count <= SEQUENCE_MIN_OUTPUTS} />
        <SeqPinButton label="Add pin +" title="Add an output"
          onClick={() => setCount(count + 1)} disabled={count >= SEQUENCE_MAX_OUTPUTS} />
      </div>
    </div>
  )
}

// ── Input Key ─────────────────────────────────────────────────────────────────
// Orange event-node scheme (fires exec-out, no exec-in)
const K_HEADER_BG     = '#7a3010'
const K_ACCENT        = '#ff7040'
const K_BORDER_SEL    = '#d04020'
const K_BORDER_IDLE   = '#5a2008'

export function InputKeyNode({ data, selected }) {
  const fired = isFired(data)
  const [listening, setListening] = useState(false)
  const stop = e => e.stopPropagation()

  useEffect(() => {
    if (!listening) return
    const handler = (e) => {
      e.preventDefault()
      e.stopPropagation()
      const key = e.key === ' ' ? 'Space' : e.key
      data.onChange?.('key', key)
      setListening(false)
    }
    window.addEventListener('keydown', handler, { once: true, capture: true })
    return () => window.removeEventListener('keydown', handler, { capture: true })
  }, [listening, data])

  const displayKey = data.key || '—'

  return (
    <div style={{
      background: selected ? '#200e08' : '#140808',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(K_BORDER_IDLE) : K_BORDER_IDLE}`,
      borderRadius: 6,
      minWidth: 180,
      fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      {/* Header */}
      <div style={{
        background: K_HEADER_BG,
        padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <span style={{ fontSize: 13 }}>⌨</span>
        <span style={{ color: K_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Input Key</span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>
      {/* Key binding */}
      <div style={{ padding: '8px 12px' }}>
        <div style={{ color: '#4a2818', fontSize: 9, marginBottom: 4, letterSpacing: 0.5 }}>KEY</div>
        <div
          onMouseDown={stop}
          onClick={(e) => { e.stopPropagation(); setListening(true) }}
          style={{
            background: listening ? '#3a1800' : '#1a0800',
            border: `1px solid ${listening ? K_ACCENT : '#3a1400'}`,
            borderRadius: 3,
            padding: '5px 10px',
            color: listening ? K_ACCENT : '#e06040',
            fontSize: 12,
            fontWeight: 'bold',
            textAlign: 'center',
            cursor: 'pointer',
            letterSpacing: 1,
            userSelect: 'none',
          }}
        >
          {listening ? 'Press any key…' : displayKey}
        </div>
      </div>
    </div>
  )
}
